import { pick } from "./i18n";
import type { ProductFeature } from "../types";

export const MODE_KEYS = [
  "staging",
  "packshot",
  "arrangements",
  "lifestyle",
  "variants",
  "dimensions",
  "callouts",
  "feature_board",
  "trust_board",
  "faq_board",
  "problem_solution_board",
  "custom",
] as const;

export type ModeKey = typeof MODE_KEYS[number];

const MODE_COLORS: Record<ModeKey, string> = {
  staging: "#d4bc94",
  packshot: "#e4e2d8",
  arrangements: "#c2cdd5",
  lifestyle: "#dde4ea",
  variants: "#2a2a26",
  dimensions: "#e8e6df",
  callouts: "#c87a4a",
  feature_board: "#d6debc",
  trust_board: "#1d2933",
  faq_board: "#3a2818",
  problem_solution_board: "#4a3d2c",
  custom: "#6d4fc2",
};

type ModeCopy = { n: string; d: string; description: string };

const modeCopyPl: Record<ModeKey, ModeCopy> = {
  staging: { n: "Produkt w scenerii", d: "jedna sceneria", description: "Produkt osadzony w jednej wybranej scenerii. Jedna grafika, a w trybie multi-shot pięć ujęć kamery tej samej sceny plus plansza zbiorcza. Do prezentacji produktu w kontekście." },
  packshot: { n: "Zdjęcie katalogowe", d: "białe tło", description: "Studyjne ujęcie na czystym tle w wybranej orientacji kamery. Jedna grafika. Do kart katalogowych i sklepu." },
  arrangements: { n: "Produkt w kilku sceneriach", d: "porównanie scen", description: "Ten sam produkt w kilku RÓŻNYCH sceneriach plus plansza porównawcza. Liczba grafik zależy od liczby scen. Do pokazania uniwersalności produktu." },
  lifestyle: { n: "Scena z osobą", d: "kontekst użycia", description: "Scena użytkowa z osobą w tle. Jedna grafika. Do komunikacji kontekstu i skali użycia." },
  variants: { n: "Warianty kolorystyczne", d: "kolory / materiały", description: "Osobne ujęcie dla każdego wariantu koloru lub materiału plus plansza porównawcza. Liczba grafik zależy od liczby wariantów." },
  dimensions: { n: "Diagram wymiarów", d: "techniczny", description: "Diagram wymiarów z adnotacjami na czystym tle. Jedna grafika. Do specyfikacji technicznej." },
  callouts: { n: "Zbliżenia detali z opisami", d: "detale", description: "Ujęcie główne plus zbliżenia detali z etykietami i plansza zbiorcza. Liczba grafik zależy od liczby detali." },
  feature_board: { n: "Plansza cech", d: "cechy produktu", description: "Plansza z ujęciem produktu i blokami najważniejszych cech. Jedna grafika." },
  trust_board: { n: "Plansza certyfikatów", d: "jakość i zaufanie", description: "Plansza zaufania: certyfikaty, normy i argumenty jakości. Jedna grafika." },
  faq_board: { n: "Plansza pytań i odpowiedzi", d: "FAQ", description: "Typograficzna plansza odpowiadająca na najczęstsze pytania o produkt. Jedna grafika." },
  problem_solution_board: { n: "Plansza problem–rozwiązanie", d: "grid 2×2", description: "Plansza 2×2 zestawiająca problem klienta z rozwiązaniem, które daje produkt. Jedna grafika." },
  custom: { n: "Własny opis", d: "opis → AI", description: "Grafika generowana według Twojego własnego opisu. Jedna grafika." },
};

const modeCopyEn: Record<ModeKey, ModeCopy> = {
  staging: { n: "Product in a scene", d: "single scene", description: "The product placed in one chosen scene. One image, or in multi-shot mode five camera angles of that same scene plus a summary board. For presenting the product in context." },
  packshot: { n: "Catalog shot", d: "white background", description: "A studio shot on a clean background in the chosen camera orientation. One image. For catalog cards and shops." },
  arrangements: { n: "Product across scenes", d: "scene comparison", description: "The same product in several DIFFERENT scenes plus a comparison board. The number of images depends on the number of scenes. To show the product's versatility." },
  lifestyle: { n: "Scene with a person", d: "usage context", description: "A usage scene with a person in the background. One image. To convey context and scale of use." },
  variants: { n: "Colour variants", d: "colours / materials", description: "A separate shot for each colour or material variant plus a comparison board. The number of images depends on the number of variants." },
  dimensions: { n: "Dimensions diagram", d: "technical", description: "A dimensions diagram with annotations on a clean background. One image. For technical specifications." },
  callouts: { n: "Detail close-ups with labels", d: "details", description: "A hero shot plus detail close-ups with labels and a summary board. The number of images depends on the number of details." },
  feature_board: { n: "Feature board", d: "product features", description: "A board with a product shot and blocks of key features. One image." },
  trust_board: { n: "Certificates board", d: "quality & trust", description: "A trust board: certificates, standards and quality arguments. One image." },
  faq_board: { n: "Q&A board", d: "FAQ", description: "A typographic board answering the most common questions about the product. One image." },
  problem_solution_board: { n: "Problem–solution board", d: "2×2 grid", description: "A 2×2 board pairing the customer's problem with the solution the product delivers. One image." },
  custom: { n: "Custom prompt", d: "your description", description: "An image generated from your own description. One image." },
};

export type Mode = ModeCopy & { k: ModeKey; color: string };

function modeCopy(): Record<ModeKey, ModeCopy> {
  return pick({ pl: modeCopyPl, en: modeCopyEn });
}

export function modes(): Mode[] {
  const copy = modeCopy();
  return MODE_KEYS.map((k) => ({ k, color: MODE_COLORS[k], ...copy[k] }));
}

export function isModeKey(value: string): value is ModeKey {
  return MODE_KEYS.includes(value as ModeKey);
}

export function modeLabel(value: string) {
  if (isModeKey(value)) return modeCopy()[value].n;
  const humanized = value.replace(/_/g, " ");
  return humanized.charAt(0).toUpperCase() + humanized.slice(1);
}

export const CONFIGURABLE_MODES: ModeKey[] = [
  "staging",
  "packshot",
  "arrangements",
  "lifestyle",
  "variants",
  "dimensions",
  "callouts",
  "feature_board",
  "trust_board",
  "faq_board",
  "problem_solution_board",
  "custom",
];

type Option = { k: string; n: string };

const STAGING_SCENES: Record<"pl" | "en", Option[]> = {
  pl: [
    { k: "auto", n: "Auto (AI dopasuje)" },
    { k: "gaming_room", n: "Pokój Gamingowy" },
    { k: "scandinavian_salon", n: "Skandynawski Salon" },
    { k: "minimalist_office", n: "Minimalistyczne Biuro" },
    { k: "industrial_loft", n: "Industrialny Loft" },
    { k: "luxury_apartment", n: "Luksusowy Apartament" },
    { k: "boho_natural", n: "Boho Naturalne" },
    { k: "garden_modern", n: "Nowoczesny Ogród" },
    { k: "patio", n: "Taras / Patio" },
    { k: "garden_wellness", n: "Ogród Wellness" },
  ],
  en: [
    { k: "auto", n: "Auto (AI decides)" },
    { k: "gaming_room", n: "Gaming Room" },
    { k: "scandinavian_salon", n: "Scandinavian Living Room" },
    { k: "minimalist_office", n: "Minimalist Office" },
    { k: "industrial_loft", n: "Industrial Loft" },
    { k: "luxury_apartment", n: "Luxury Apartment" },
    { k: "boho_natural", n: "Natural Boho" },
    { k: "garden_modern", n: "Modern Garden" },
    { k: "patio", n: "Terrace / Patio" },
    { k: "garden_wellness", n: "Garden Wellness" },
  ],
};

export function stagingScenes() {
  return pick(STAGING_SCENES);
}

const PACKSHOT_ORIENTATIONS: Record<"pl" | "en", Option[]> = {
  pl: [
    { k: "3quarter_left", n: "3/4 lewy (standard)" },
    { k: "3quarter_right", n: "3/4 prawy" },
    { k: "front", n: "Przód (frontal)" },
    { k: "side_left", n: "Bok lewy" },
  ],
  en: [
    { k: "3quarter_left", n: "3/4 left (standard)" },
    { k: "3quarter_right", n: "3/4 right" },
    { k: "front", n: "Front (frontal)" },
    { k: "side_left", n: "Left side" },
  ],
};

export function packshotOrientations() {
  return pick(PACKSHOT_ORIENTATIONS);
}

const LIFESTYLE_SCENES: Record<"pl" | "en", Option[]> = {
  pl: [
    { k: "garden_patio", n: "Ogród / taras" },
    { k: "wellness_garden", n: "Wellness w ogrodzie" },
    { k: "home_living", n: "Dom / salon" },
    { k: "office_workspace", n: "Biuro / workspace" },
    { k: "lounge_relax", n: "Strefa relaksu" },
    { k: "universal", n: "Uniwersalne użycie" },
  ],
  en: [
    { k: "garden_patio", n: "Garden / terrace" },
    { k: "wellness_garden", n: "Garden wellness" },
    { k: "home_living", n: "Home / living room" },
    { k: "office_workspace", n: "Office / workspace" },
    { k: "lounge_relax", n: "Lounge / relax" },
    { k: "universal", n: "Universal use" },
  ],
};

export function lifestyleScenes() {
  return pick(LIFESTYLE_SCENES);
}

const ARRANGEMENT_SCENE_KEYS = [
  "scandinavian_salon",
  "minimalist_office",
  "industrial_loft",
  "luxury_apartment",
  "garden_modern",
  "patio",
];

const ARRANGEMENT_SCENES: Record<"pl" | "en", Option[]> = {
  pl: [
    { k: "scandinavian_salon", n: "Salon skandynawski" },
    { k: "minimalist_office", n: "Nowoczesne biuro" },
    { k: "industrial_loft", n: "Loft industrialny" },
    { k: "luxury_apartment", n: "Luksusowy apartament" },
    { k: "garden_modern", n: "Nowoczesny ogród" },
    { k: "patio", n: "Taras / patio" },
  ],
  en: [
    { k: "scandinavian_salon", n: "Scandinavian living room" },
    { k: "minimalist_office", n: "Modern office" },
    { k: "industrial_loft", n: "Industrial loft" },
    { k: "luxury_apartment", n: "Luxury apartment" },
    { k: "garden_modern", n: "Modern garden" },
    { k: "patio", n: "Terrace / patio" },
  ],
};

export function arrangementScenes() {
  return pick(ARRANGEMENT_SCENES);
}

const ARRANGEMENT_DOMAIN_DEFAULTS: Record<string, string[]> = {
  office_furniture: ["minimalist_office", "industrial_loft"],
  indoor_furniture: ["scandinavian_salon", "luxury_apartment"],
  garden_furniture: ["garden_modern", "patio"],
  garden_wellness: ["garden_modern", "patio"],
  home_garden: ["patio", "scandinavian_salon"],
};

export const DEFAULT_ARRANGEMENT_SCENE_KEYS = ARRANGEMENT_SCENE_KEYS.slice(0, 2);

export function defaultArrangementSceneKeys(productContextKey: string | null | undefined): string[] {
  return ARRANGEMENT_DOMAIN_DEFAULTS[productContextKey ?? ""] ?? DEFAULT_ARRANGEMENT_SCENE_KEYS;
}

const BOARD_LAYOUTS: Record<"pl" | "en", Option[]> = {
  pl: [
    { k: "auto", n: "Auto (AI dopasuje)" },
    { k: "grid", n: "Siatka" },
    { k: "row", n: "Rząd" },
    { k: "collage", n: "Kolaż" },
  ],
  en: [
    { k: "auto", n: "Auto (AI decides)" },
    { k: "grid", n: "Grid" },
    { k: "row", n: "Row" },
    { k: "collage", n: "Collage" },
  ],
};

export function boardLayouts() {
  return pick(BOARD_LAYOUTS);
}

const BOARD_STYLES: Record<"pl" | "en", Option[]> = {
  pl: [
    { k: "auto", n: "Auto (AI dopasuje)" },
    { k: "light_studio", n: "Jasne studio" },
    { k: "warm_editorial", n: "Ciepły editorial" },
    { k: "dark_premium", n: "Ciemny premium" },
  ],
  en: [
    { k: "auto", n: "Auto (AI decides)" },
    { k: "light_studio", n: "Light studio" },
    { k: "warm_editorial", n: "Warm editorial" },
    { k: "dark_premium", n: "Dark premium" },
  ],
};

export function boardStyles() {
  return pick(BOARD_STYLES);
}

const CONTEXT_OPTIONS: Record<"pl" | "en", Option[]> = {
  pl: [
    { k: "auto", n: "Auto (AI rozpoznaje)" },
    { k: "office_furniture", n: "Meble biurowe" },
    { k: "indoor_furniture", n: "Meble do wnętrz" },
    { k: "garden_furniture", n: "Meble ogrodowe" },
    { k: "garden_wellness", n: "Wellness ogrodowy" },
    { k: "home_garden", n: "Dom i ogród" },
    { k: "custom", n: "Własny kontekst" },
  ],
  en: [
    { k: "auto", n: "Auto (AI detects)" },
    { k: "office_furniture", n: "Office furniture" },
    { k: "indoor_furniture", n: "Indoor furniture" },
    { k: "garden_furniture", n: "Garden furniture" },
    { k: "garden_wellness", n: "Garden wellness" },
    { k: "home_garden", n: "Home & garden" },
    { k: "custom", n: "Custom context" },
  ],
};

export function contextOptions() {
  return pick(CONTEXT_OPTIONS);
}

type EstimateProduct = {
  variants_json?: Array<{ name: string; description?: string | undefined }> | null | undefined;
  features_json?: ProductFeature[] | null | undefined;
};

function nonEmptyLineCount(text: string | undefined): number {
  return (typeof text === "string" ? text : "").split("\n").map((line) => line.trim()).filter(Boolean).length;
}

export function estimateImageCalls(mode: ModeKey, config: Record<string, string> | undefined, product: EstimateProduct): number {
  if (mode === "staging") {
    if (config?.multi_shot !== "true") return 1;
    return config?.skip_board === "true" ? 5 : 6;
  }

  if (mode === "variants") {
    const count = nonEmptyLineCount(config?.custom_variants) || product.variants_json?.length || 1;
    return config?.skip_board === "true" ? count : count + 1;
  }

  if (mode === "arrangements") {
    let scenes: number;
    if (config?.scene_source === "custom") {
      const customLines = nonEmptyLineCount(config.custom_scenes);
      scenes = customLines > 0 ? Math.min(4, customLines) : DEFAULT_ARRANGEMENT_SCENE_KEYS.length;
    } else if (config?.scene_keys) {
      scenes = config.scene_keys.split(",").map((key) => key.trim()).filter(Boolean).length;
    } else {
      scenes = DEFAULT_ARRANGEMENT_SCENE_KEYS.length;
    }
    return config?.skip_board === "true" ? scenes : scenes + 1;
  }

  if (mode === "callouts") {
    const details = Math.min(4, nonEmptyLineCount(config?.features) || product.features_json?.length || 4);
    return config?.skip_board === "true" ? 1 + details : 1 + details + 1;
  }

  return 1;
}
